// The sections of the office, and where each role enters (D-240, D-273).
//
// One list, read by the shell to send a role to its entry and by the office
// to draw its menu. A section names the module it belongs to: the menu shows
// only the sections that exist and whose module the organization has on,
// and the administration enters the first of them (D-240).
// INC-09c-1: Informes came second (D-273). INC-11a-1 (E.1): the Tablero and
// the organization's sites go before both, bound to no module, so the
// administration enters the Tablero. INC-11b (E.1, E.6): the agenda follows
// the sites, bound to planning. INC-11c-1 (E.7, 14.G point 6): the
// configuration goes last, bound to no module, with its own submenu. INC-10b
// (B1): the external records after Informes, bound to their module. Nothing
// of the office's code is here:
// the shell must know where to send a person without loading the office
// (D-83).
import type { MeResponse } from '../api/types';
import type { IconName } from '../ui/states';

export type SectionKey =
  'dashboard' | 'sites' | 'agenda' | 'alerts' | 'reports' | 'records' | 'configuration';

export type ModuleCode = MeResponse['modules'][number];

export interface OfficeSection {
  key: SectionKey;
  /** The route of the section, under which its own screens live. */
  path: string;
  /** The module that has to be on for the section to exist (D-273); none for
   *  a section of the core, which every organization has. */
  module: ModuleCode | null;
  /** The key of the catalog with the section's name, which may name the
   *  organization's own word for its sites (`{{sites}}`). */
  labelKey: string;
  icon: IconName;
}

/** INC-11a-1 (E.2, D-240): what the administration enters. */
const DASHBOARD: OfficeSection = {
  key: 'dashboard',
  path: '/tablero',
  module: null,
  labelKey: 'office.sections.dashboard',
  icon: 'dashboard',
};

/** INC-14a-3b-1 (point 11): where the organization's logo leads in the office. */
export const DASHBOARD_SECTION_PATH = DASHBOARD.path;

/** INC-11a-1 (E.3): the portfolio, by the organization's word for its sites. */
const SITES: OfficeSection = {
  key: 'sites',
  path: '/instalaciones',
  module: null,
  labelKey: 'office.sections.sites',
  icon: 'site',
};

/** INC-11b (E.6): the agenda of every site and of every operator. */
const AGENDA: OfficeSection = {
  key: 'agenda',
  path: '/agenda',
  module: 'planning',
  labelKey: 'office.sections.agenda',
  icon: 'calendar',
};

const ALERTS: OfficeSection = {
  key: 'alerts',
  path: '/alertas',
  module: 'alerts',
  labelKey: 'office.sections.alerts',
  icon: 'bell',
};

/** INC-09c-1: the queue of approval, and the review of one report under it. */
const REPORTS: OfficeSection = {
  key: 'reports',
  path: '/informes',
  module: 'reports',
  labelKey: 'office.sections.reports',
  icon: 'file',
};

/** INC-10b (B1, D-273): the documents of third parties, with their module on;
 *  the validation of one under it. */
const RECORDS: OfficeSection = {
  key: 'records',
  path: '/registros-externos',
  module: 'external_records',
  labelKey: 'office.sections.records',
  icon: 'document',
};

/** INC-11c-1 (E.7): the organization's configuration, Personas first; the rest of
 *  its submenu arrives with 11c-2. */
const CONFIGURATION: OfficeSection = {
  key: 'configuration',
  path: '/configuracion',
  module: null,
  labelKey: 'office.sections.configuration',
  icon: 'settings',
};

export const OFFICE_SECTIONS: readonly OfficeSection[] = [
  DASHBOARD,
  SITES,
  AGENDA,
  ALERTS,
  REPORTS,
  RECORDS,
  CONFIGURATION,
];

/** The sections an organization shows: the core's, and the ones whose module is on. */
export function sectionsFor(modules: readonly string[]): OfficeSection[] {
  return OFFICE_SECTIONS.filter(
    (section) => section.module === null || modules.includes(section.module),
  );
}

/** Where the administration enters: its first section, the Tablero (D-240),
 *  which every organization has — never the field and never the waiting screen. */
export function officeHome(modules: readonly string[]): string {
  return (sectionsFor(modules)[0] ?? DASHBOARD).path;
}

/** The section a path is under, when it is under one. */
export function sectionOf(pathname: string): OfficeSection | undefined {
  return OFFICE_SECTIONS.find(
    (section) => pathname === section.path || pathname.startsWith(`${section.path}/`),
  );
}

/** INC-12c-1: where the end customer's portal lives; its own screens are
 *  under it (`web/src/portal/paths.ts`). Here so the shell sends a role there
 *  without loading the portal (D-83). */
export const PORTAL_PATH = '/portal';

/** Where a role enters once signed in (D-172, D-240): operations the field,
 *  administration its first section, the end customer the portal's Inicio
 *  (INC-12c-1). */
export function homeOf(role: MeResponse['role'], modules: readonly string[]): string {
  if (role === 'admin') return officeHome(modules);
  if (role === 'client') return PORTAL_PATH;
  return '/';
}
